export const ISO_NOT_AVAILABLE = 'N/A'

export const ISO_YEARS = ['2005', '2010', '2017']

export const ISO_CATALOG = {
  HSDO: {
    'DMA (max 1.5% S)': ['2005', '2010'],
  },
  HSFO: {
    'RME 180 (max 3.5% S)': ['2010', '2017'],
    'RMG 180 (max 3.5% S)': ['2010', '2017'],
    'RMG 380 (max 3.5% S)': ['2005', '2010', '2017'],
    'RMK 500 (max 3.5% S)': ['2010', '2017'],
    'RMK 700 (max 3.5% S)': ['2005', '2010', '2017'],
  },
  LSDO: {
    'DMA (max 0.1% S)': ['2005', '2010', '2017'],
    'DMA (max 1.5% S)': ['2017'],
    'DMB (max 0.1% S)': ['2005', '2010', '2017'],
  },
  ULSFO: {
    'RMD 80 (max 0.1% S)': ['2005', '2010', '2017'],
    'RME 180 (max 0.1% S)': ['2005', '2010', '2017'],
    'RME 180 (max 3.5% S)': ['2005'],
    'RMG 180 (max 0.1% S)': ['2010', '2017'],
    'RMG 380 (max 0.1% S)': ['2005', '2010', '2017'],
  },
  VLSFO: {
    'RMD 80 (max 0.5% S)': ['2005', '2010', '2017'],
    'RME 180 (max 0.5% S)': ['2010', '2017'],
    'RMG 180 (max 0.5% S)': ['2010', '2017'],
    'RMG 380 (max 0.5% S)': ['2005', '2010', '2017'],
    'RMG 380 (max 0.5% S) BIO10': ['2017'],
    'RMG 380 (max 0.5% S) BIO20': ['2017'],
    'RMG 380 max. 0.50%S BIO24': ['2017'],
    'RMG 380 max. 0.50%S BIO30': ['2017'],
    'RMK 500 (max 0.5% S)': ['2010', '2017'],
    'RMK 500 (max 0.5% S) BIO20': ['2017'],
    'RMK 500 (max 0.5% S) BIO30': ['2017'],
    'RMK 500 (max 0.5% S) BIO45': ['2017'],
    'RMK 700 (max 0.5% S)': ['2005'],
  },
}

export const isOtherFuel = fuelGrade =>
  String(fuelGrade || '')
    .trim()
    .toUpperCase() === 'OTHER'

export const isBioFuel = fuelGrade =>
  String(fuelGrade || '')
    .toUpperCase()
    .includes('BIO')

export const isDistillateKind = kind => kind === 'HSDO' || kind === 'LSDO'

export const isResidualKind = kind =>
  kind === 'VLSFO' || kind === 'HSFO' || kind === 'ULSFO'

export const getIsoYears = (kind, fuelGrade) => {
  if (!kind || !fuelGrade) return []
  const yearsForGrade = ISO_CATALOG[kind]?.[fuelGrade] || []

  return ISO_YEARS.filter(year => yearsForGrade.includes(year))
}

export const recalculateIsoSelection = ({ kind, fuelGrade, currentIso }) => {
  const options = getIsoYears(kind, fuelGrade)
  const otherFuel = isOtherFuel(fuelGrade)

  if (!kind || !fuelGrade || otherFuel) {
    return {
      isoDisabled: true,
      options,
      value: '',
    }
  }

  if (options.length === 0) {
    return {
      isoDisabled: true,
      options,
      value: ISO_NOT_AVAILABLE,
    }
  }

  if (isBioFuel(fuelGrade) && options.length === 1) {
    return {
      isoDisabled: false,
      options,
      value: options[0],
    }
  }

  if (options.includes(currentIso)) {
    return {
      isoDisabled: false,
      options,
      value: currentIso,
    }
  }

  return {
    isoDisabled: false,
    options,
    value: '',
  }
}

// ─── ISO 8217 Limit Catalog ────────────────────────────────────────────────────
// _base: 2005-style entries (no water/sulphur/TSA/TSP/CCAI/TAN limits)
const _base = (density, viscosity, mcr, ash, vanadium, alSi) => ({
  aluminium: 'report',
  aluminiumSilicon: `max ${alSi}`,
  ashContent: `max ${ash}`,
  calcium: 'max 30',
  ccai: '-',
  density15: `max ${density}`,
  fame: '-',
  flashpoint: 'min 60',
  iron: 'report',
  lead: 'report',
  lowHeatingValue: '-',
  magnesium: 'report',
  microCarbonResidue: `max ${mcr}`,
  netCalorificValue: 'report',
  nickel: 'report',
  phosphorous: 'max 15',
  potassium: '-',
  pourpoint: 'W: 30; S:30',
  silicon: 'report',
  sodium: '-',
  stabilityIndex: 'report',
  sulphurContent: '-',
  totalAcidNumber: '-',
  totalSedAccelerated: '-',
  totalSedExistent: '-',
  totalSedPotential: '-',
  vanadium: `max ${vanadium}`,
  viscosity50: `max ${viscosity}`,
  waterContent: '-',
  zinc: 'max 15',
})

// _full: 2010/2017-style entries (includes water/sulphur/TSA/TSP/CCAI/TAN limits)
const _full = (
  density,
  viscosity,
  mcr,
  ash,
  sulphur,
  sodium,
  vanadium,
  ccai,
  alSi
) => ({
  aluminium: 'report',
  aluminiumSilicon: `max ${alSi}`,
  ashContent: `max ${ash}`,
  calcium: 'max 30',
  ccai: `max ${ccai}`,
  density15: `max ${density}`,
  fame: '-',
  flashpoint: 'min 60',
  iron: 'report',
  lead: 'report',
  lowHeatingValue: '-',
  magnesium: 'report',
  microCarbonResidue: `max ${mcr}`,
  netCalorificValue: 'report',
  nickel: 'report',
  phosphorous: 'max 15',
  potassium: '-',
  pourpoint: 'W: 30; S:30',
  silicon: 'report',
  sodium: `max ${sodium}`,
  stabilityIndex: 'report',
  sulphurContent: `max ${sulphur}`,
  totalAcidNumber: 'max 2,5',
  totalSedAccelerated: '0,1',
  totalSedExistent: '-',
  totalSedPotential: '0,1',
  vanadium: `max ${vanadium}`,
  viscosity50: `max ${viscosity}`,
  waterContent: 'max 0,5',
  zinc: 'max 15',
})

// _dist: distillate (LSDO/HSDO) entries — viscosity40/mcrt/cetaneIndex/lubricity/appearance
// water: pass '-' when the standard has no limit, or the full 'max x,y' string when limited
const _dist = (
  density,
  viscosity40,
  pour,
  mcrt,
  ash,
  water,
  sulphur,
  cetane,
  tan,
  lubricity
) => ({
  aluminium: 'not tested',
  aluminiumSilicon: 'not tested',
  appearance: 'report',
  ashContent: `max ${ash}`,
  calcium: 'not tested',
  ccai: '-',
  cetaneIndex: `min ${cetane}`,
  density15: `max ${density}`,
  fame: '-',
  flashpoint: 'min 60',
  iron: 'not tested',
  lead: 'not tested',
  lowHeatingValue: '-',
  lubricity,
  magnesium: 'not tested',
  mcrt10pctResidue: `max ${mcrt}`,
  microCarbonResidue: '-',
  netCalorificValue: 'report',
  nickel: 'not tested',
  phosphorous: 'not tested',
  potassium: '-',
  pourpoint: pour,
  silicon: 'not tested',
  sodium: 'not tested',
  stabilityIndex: '-',
  sulphurContent: `max ${sulphur}`,
  totalAcidNumber: `max ${tan}`,
  totalSedAccelerated: '-',
  totalSedExistent: '-',
  totalSedPotential: '-',
  vanadium: 'not tested',
  viscosity40,
  viscosity50: '-',
  waterContent: water,
  zinc: 'not tested',
})

export const ISO_LIMITS = {
  HSDO: {
    'DMA (max 1.5% S)': {
      2005: _dist(
        890,
        '1,5 > x < 6',
        'W: -6; S:0',
        '0,3',
        '0,01',
        '-',
        '1,5',
        40,
        '0,5',
        '-'
      ),
      2010: _dist(
        890,
        '2 > x < 6',
        'W: -6; S:0',
        '0,3',
        '0,01',
        '-',
        '1,5',
        40,
        '0,5',
        '520 (only test if sulphur content <0,05)'
      ),
    },
  },
  HSFO: {
    'RME 180 (max 3.5% S)': {
      2010: _full(991, 180, 15, '0,07', '3,5', 50, 150, 860, 60),
      2017: _full(991, 180, 15, '0,07', '3,5', 50, 150, 860, 60),
    },
    'RMG 180 (max 3.5% S)': {
      2010: _full(991, 180, 18, '0,1', '3,5', 100, 350, 870, 60),
      2017: _full(991, 180, 18, '0,1', '3,5', 100, 350, 870, 60),
    },
    'RMG 380 (max 3.5% S)': {
      2005: _base(991, 380, 18, '0,15', 300, 60),
      2010: _full(991, 380, 18, '0,1', '3,5', 100, 350, 870, 60),
      2017: _full(991, 380, 18, '0,1', '3,5', 100, 350, 870, 60),
    },
    'RMK 500 (max 3.5% S)': {
      2010: _full(1010, 500, 18, '0,1', '3,5', 100, 350, 870, 60),
      2017: _full(1010, 500, 20, '0,15', '3,5', 100, 450, 870, 60),
    },
    'RMK 700 (max 3.5% S)': {
      2005: _base(1010, 700, 22, '0,15', 600, 60),
      2010: _full(1010, 700, 20, '0,15', '3,5', 100, 450, 870, 60),
      2017: _full(1010, 700, 20, '0,15', '3,5', 100, 450, 870, 60),
    },
  },
  LSDO: {
    'DMA (max 0.1% S)': {
      2005: _dist(
        890,
        '1,5 > x < 6',
        'W: -6; S:0',
        '0,3',
        '0,01',
        '-',
        '0,1',
        40,
        '0,5',
        '-'
      ),
      2010: _dist(
        890,
        '2 > x < 6',
        'W: -6; S:0',
        '0,3',
        '0,01',
        '-',
        '0,1',
        40,
        '0,5',
        '520 (only test if sulphur content <0,05)'
      ),
      2017: _dist(
        890,
        '2 > x < 6',
        'W: -6; S:0',
        '0,3',
        '0,01',
        '-',
        '0,1',
        40,
        '0,5',
        '520 (only test if sulphur content <0,05)'
      ),
    },
    'DMA (max 1.5% S)': {
      2017: _dist(
        890,
        '2 > x < 6',
        'W: -6; S:0',
        '0,3',
        '0,01',
        '-',
        '1,5',
        40,
        '0,5',
        '520 (only test if sulphur content <0,05)'
      ),
    },
    'DMB (max 0.1% S)': {
      2005: _dist(
        900,
        '< 11',
        'W: 0; S:6',
        '0,3',
        '0,01',
        'max 0,3',
        '0,1',
        35,
        '0,5',
        '-'
      ),
      2010: _dist(
        900,
        '2 > x < 11',
        'W: 0; S:6',
        '0,3',
        '0,01',
        'max 0,3',
        '0,1',
        35,
        '0,5',
        '520 (only test if sulphur content <0,05)'
      ),
      2017: _dist(
        900,
        '2 > x < 11',
        'W: 0; S:6',
        '0,3',
        '0,01',
        'max 0,3',
        '0,1',
        35,
        '0,5',
        '520 (only test if sulphur content <0,05)'
      ),
    },
  },
  ULSFO: {
    'RMD 80 (max 0.1% S)': {
      2005: _base(980, 80, 14, '0,1', 350, 60),
      2010: _full(975, 80, 14, '0,07', '0,1', 100, 150, 860, 60),
      2017: _full(975, 80, 14, '0,07', '0,1', 100, 150, 860, 60),
    },
    'RME 180 (max 0.1% S)': {
      2005: _base(991, 180, 15, '0,1', 200, 60),
      2010: _full(991, 180, 15, '0,07', '0,1', 50, 150, 860, 60),
      2017: _full(991, 180, 15, '0,07', '0,1', 50, 150, 860, 60),
    },
    'RME 180 (max 3.5% S)': {
      2005: _base(991, 180, 15, '0,1', 200, 60),
    },
    'RMG 180 (max 0.1% S)': {
      2010: _full(991, 180, 18, '0,1', '0,1', 100, 350, 870, 60),
      2017: _full(991, 180, 18, '0,1', '0,1', 100, 350, 870, 60),
    },
    'RMG 380 (max 0.1% S)': {
      2005: _base(991, 380, 18, '0,15', 300, 60),
      2010: _full(991, 380, 18, '0,1', '0,1', 100, 350, 870, 60),
      2017: _full(991, 380, 18, '0,1', '0,1', 100, 350, 870, 60),
    },
  },
  VLSFO: {
    'RMD 80 (max 0.5% S)': {
      2005: _base(980, 80, 14, '0,1', 350, 60),
      2010: _full(975, 80, 14, '0,07', '0,5', 100, 150, 860, 60),
      2017: _full(975, 80, 14, '0,07', '0,5', 100, 150, 860, 60),
    },
    'RME 180 (max 0.5% S)': {
      2010: _full(991, 180, 15, '0,07', '0,5', 50, 150, 860, 60),
      2017: _full(991, 180, 15, '0,07', '0,5', 50, 150, 860, 60),
    },
    'RMG 180 (max 0.5% S)': {
      2010: _full(991, 180, 18, '0,1', '0,5', 100, 350, 870, 60),
      2017: _full(991, 180, 18, '0,1', '0,5', 100, 350, 870, 60),
    },
    'RMG 380 (max 0.5% S)': {
      2005: _base(991, 380, 18, '0,15', 300, 60),
      2010: _full(991, 380, 18, '0,1', '0,5', 100, 350, 870, 60),
      2017: _full(991, 380, 18, '0,1', '0,5', 100, 350, 870, 60),
    },
    'RMG 380 (max 0.5% S) BIO10': {
      2017: _full(991, 380, 18, '0,1', '0,5', 100, 350, 870, 60),
    },
    'RMG 380 (max 0.5% S) BIO20': {
      2017: _full(991, 380, 18, '0,1', '0,5', 100, 350, 870, 60),
    },
    'RMG 380 max. 0.50%S BIO24': {
      2017: _full(991, 380, 18, '0,1', '0,5', 100, 350, 870, 60),
    },
    'RMG 380 max. 0.50%S BIO30': {
      2017: _full(991, 380, 18, '0,1', '0,5', 100, 350, 870, 60),
    },
    'RMK 500 (max 0.5% S)': {
      2010: _full(1010, 500, 18, '0,1', '0,5', 100, 350, 870, 60),
      2017: _full(1010, 500, 20, '0,15', '0,5', 100, 450, 870, 60),
    },
    'RMK 500 (max 0.5% S) BIO20': {
      2017: _full(1010, 500, 20, '0,15', '0,5', 100, 450, 870, 60),
    },
    'RMK 500 (max 0.5% S) BIO30': {
      2017: _full(1010, 500, 20, '0,15', '0,5', 100, 450, 870, 60),
    },
    'RMK 500 (max 0.5% S) BIO45': {
      2017: _full(1010, 500, 20, '0,15', '0,5', 100, 450, 870, 60),
    },
    'RMK 700 (max 0.5% S)': {
      2005: _base(1010, 700, 22, '0,15', 600, 60),
    },
  },
}

/**
 * Returns the ISO 8217 limit string for a given kind / fuel grade / ISO year / test key.
 * Returns '-' when no limit is defined (not applicable, no data, or no selection).
 */
export const getTestLimit = (kind, fuelGrade, isoYear, testKey) => {
  if (!kind || !fuelGrade || !isoYear || isoYear === ISO_NOT_AVAILABLE)
    return '-'

  return ISO_LIMITS[kind]?.[fuelGrade]?.[isoYear]?.[testKey] ?? '-'
}
